import { useContext, useMemo } from 'react';
import ThemeContext from './ThemeContext';
import { useReadingDirections } from './utils/readingDirections';
var shmonsole = console;
/**
 * Hook used to call "buildStyle" function in a "useStyle" hook.
 * Use this Hook with the "useStyle/buildStyle" css-in-js pattern.
 *
 * Note: In Typescript with Emotion you should have your buildStyle function return your object with the `as const`
 * assertion to have emotion do the type checking for you.
 *
 * @param {BuildStyle} buildStyle
 * @param {?{isRtl: IsRtl, errorId: ErrorId, errorStyle: ErrorStyle, suppressErrors: SuppressErrors}} options
 * @returns {(StyleObject | ErrorStyle)}
 *
 * @example function useStyle(state) {
 *  const { containerElement, innerElement } = useBuildStyleWithTheme(buildStyle, { isRtl: state.isRtl });
 *  return {
 *    containerElementStyle: containerElement.base,
 *    innerElementStyle: { ...innerElement.base, ...(state.isOpen && innerElement.isOpen) }
 *  };
 * }
 * function buildStyle(theme, { start, end, readingDirection }) {
 *  return {
 *    containerElement: {
 *      base: {
 *        ...readingDirection,
 *        backgroundColor: theme.backgroundColor.surface
 *      }
 *    },
 *    innerElement: {
 *      base: {
 *        [start.padding]: 8,
 *        [end.padding]: 16
 *      },
 *      isOpen: {
 *        [start.padding]: 16
 *      }
 *    }
 *  };
 * }
 *
 * @typedef { Object } Tokens
 * @typedef { (tokens: Tokens, readingDirections: { start: Direction, end: Direction, readingDirection }) => StyleObject } BuildStyle A pure function used to add themed tokens to style properties
 * @typedef { boolean } IsRtl for right-to-left languages
 * @typedef { Object } StyleObject Object return from buildStyle function (has theme applied)
 * @typedef { Object } ErrorStyle Fallback style object, to be returned in case of error
 * @typedef { string } ErrorId String added to error message for debugging purposes
 * @typedef { boolean } SuppressErrors Don't log errors (fail silently)
 */
export function useBuildStyleWithTheme(buildStyle, _a) {
    var _b = _a === void 0 ? {} : _a, isRtl = _b.isRtl, errorId = _b.errorId, errorStyle = _b.errorStyle, _c = _b.suppressErrors, suppressErrors = _c === void 0 ? false : _c;
    var theme = useContext(ThemeContext);
    var readingDirections = useReadingDirections(isRtl);
    /** @type {StyleObject} */
    var styleObject = useMemo(function () {
        if (typeof buildStyle === 'function') {
            try {
                return buildStyle(theme, readingDirections);
            }
            catch (error) {
                if (!suppressErrors) {
                    shmonsole.error("[useBuildStyleWithTheme" + (errorId ? ": " + errorId : '') + "] \"buildStyle function has errors. \nThis is likely due to the tokens object given to ThemProvider not matching the expected shape. Or, the ThemeContext cannot find a ThemeProvider instance in the component tree (Provider and Consumer dependency versions must match)." + (theme.version
                        ? " Found tokens with version: " + theme.version
                        : ''));
                    if (error instanceof Error) {
                        Error(error.message);
                    }
                }
                return errorStyle;
            }
        }
        if (!suppressErrors) {
            shmonsole.error("[useBuildStyleWithTheme" + (errorId ? ": " + errorId : '') + "] \"buildStyle\" parameter is not a function");
        }
        return errorStyle;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        buildStyle,
        errorId,
        errorStyle,
        isRtl,
        readingDirections,
        suppressErrors,
        theme
    ]);
    return styleObject;
}
